/* eslint-disable react-hooks/exhaustive-deps */
import React, { createRef, useEffect, useState } from "react";
import Body from "../../components/Body";
import "../../styles/poRequest.scss";
import moment from "moment";
import { css } from "@emotion/react";
import { AiFillPrinter } from "react-icons/ai";
import numberFormat from "../../common/numberFormat";
import addPrecision from "../../common/addPrecision";
import { Helmet } from "react-helmet-async";
import removeCommas from "../../common/removeCommas";
import LetterHeader from "../../components/LetterHeader";

import AppButton from "../../common/AppButton";
import AppInput from "../../common/AppInput";
import { cloneDeep, forEach, groupBy, sum } from "lodash";
import api from "../../config/api";
import useJob from "../../hooks/useJob";
import { Checkbox } from "@mui/material";
import LabelText from "../../common/LabelText";
import SimpleExpansionPanel from "../../common/SimpleExpansionPanel";
import Table from "../../common/Table";
import {
  salesHeader,
  rebatesHeader,
  salaryHeader,
  otherExpensesHeader,
  jobHeader,
  incomeList,
  paymentDues,
  salesHST,
} from "./StatementTables";
import { RiseLoader } from "react-spinners";
import colors from "../../config/colors";
import { useLocation } from "react-router-dom";
import { usePDF } from "react-to-pdf";
import { useQuery } from "@tanstack/react-query";
import { getJobs } from "../../stores/JobStore";
import { getPayments } from "../../stores/PaymentStore";
import { getRebates } from "../../stores/RebateStore";
import { getExpenses } from "../../stores/ExpensesStore";
import { getSalaries } from "../../stores/SalaryStore";
import { getOrganizations } from "../../stores/OrganizationStore";
import RebateStatementTemplate from "./RebateStatementTemplate";
import { getContractInvocies } from "../../stores/ContractInvocieStore";

const today = moment();
const formInit = {
  from: moment(`01/${today.format("MM/YYYY")}`, "DD/MM/YYYY").toDate(),
  to: new Date(),
};
export default function RebateStatement() {
  const [data, setData] = useState();
  const [form, setForm] = useState(cloneDeep(formInit));
  const [salesIncome, setSalesIncome] = useState([]);
  const [salesIncomeList, setSalesIncomeList] = useState([]);
  const [duePayments, setDuePayments] = useState([]);
  const [duePaymentsList, setDuePaymentsList] = useState([]);
  const [memoryList, setMemoryList] = useState({});
  const [isPrintMode, setIsPrintMode] = useState(false);

  const { data: organizationsData } = useQuery({
    queryFn: () => getOrganizations(),
    queryKey: ["organizations"],
  });

  const { data: jobPaymentsData } = useQuery({
    queryFn: () => getPayments({ startDate: form.from, endDate: form.to }),
    queryKey: ["jobPayments", form.from, form.to],
  });

  const { data: globalJobPaymentsData } = useQuery({
    queryFn: () => getPayments({}),
    queryKey: ["jobPayments"],
  });

  const { data: jobsData } = useQuery({
    queryFn: () =>
      getJobs({
        startDate: form.from,
        endDate: form.to,
        middleDateType: "invoiceDateAsDate",
        advance: true,
      }),
    queryKey: ["jobs", form.from, form.to, "invoiceDateAsDate"],
  });

  const { data: dueInvsData } = useQuery({
    queryFn: () =>
      getJobs({
        startDate: new Date(new Date().setFullYear(form.to.getFullYear() - 3)),
        endDate: form.to,
        middleDateType: "invoiceDateAsDate",
        advance: true,
      }),
    queryKey: ["jobs", "due", form.to, "invoiceDateAsDate"],
  });

  const { data: contInvs } = useQuery({
    queryFn: () =>
      getContractInvocies({
        advance: true,
        startDate: form.from,
        endDate: form.to,
      }),
    queryKey: ["contractInvoices", form.from, form.to, "createdAt"],
  });

  const { data: dueContInvs } = useQuery({
    queryFn: () =>
      getContractInvocies({
        startDate: new Date(new Date().setFullYear(form.to.getFullYear() - 3)),
        endDate: form.to,
        advance: true,
      }),
    queryKey: ["contractInvoices", "due", form.to, "createdAt"],
  });

  const { data: endDateBasedJobsData } = useQuery({
    queryFn: () =>
      getJobs({
        startDate: form.from,
        endDate: form.to,
        middleDateType: "jobEndDateAsDate",
        advance: true,
      }),
    queryKey: ["jobs", form.from, form.to, "jobEndDate"],
  });

  const { data: rebatesData } = useQuery({
    queryFn: () => getRebates({ startDate: form.from, endDate: form.to }),
    queryKey: ["rebates", form.from, form.to],
  });

  const { data: expensesData } = useQuery({
    queryFn: () => getExpenses({ startDate: form.from, endDate: form.to }),
    queryKey: ["expenses", form.from, form.to],
  });

  const { data: salariesData } = useQuery({
    queryFn: () => getSalaries({ startDate: form.from, endDate: form.to }),
    queryKey: ["salaries", form.from, form.to],
  });

  const { data: organizations } = useQuery({
    queryFn: () => getOrganizations(),
    queryKey: ["organizations", form.from, form.to],
  });

  useEffect(() => {
    if (jobsData) {
      setMemoryList((cur) => ({ ...cur, invs: jobsData }));
    }
  }, [jobsData]);

  useEffect(() => {
    if (dueInvsData) {
      setMemoryList((cur) => ({ ...cur, dueInvsData }));
    }
  }, [dueInvsData]);

  useEffect(() => {
    if (contInvs) {
      setMemoryList((cur) => ({ ...cur, contInvs }));
    }
  }, [contInvs]);

  useEffect(() => {
    if (dueContInvs) {
      setMemoryList((cur) => ({ ...cur, dueContInvs }));
    }
  }, [dueContInvs]);

  useEffect(() => {
    if (jobPaymentsData) {
      setSalesIncomeList(jobPaymentsData);
    }
  }, [jobPaymentsData]);

  useEffect(() => {
    if (rebatesData) {
      setMemoryList((cur) => ({ ...cur, rebates: rebatesData }));
    }
  }, [rebatesData]);

  useEffect(() => {
    if (expensesData) {
      setMemoryList((cur) => ({ ...cur, expenses: expensesData }));
    }
  }, [expensesData]);

  useEffect(() => {
    if (salariesData) {
      setMemoryList((cur) => ({ ...cur, salary: salariesData }));
    }
  }, [salariesData]);

  useEffect(() => {
    if (endDateBasedJobsData) {
      setMemoryList((cur) => ({ ...cur, endDateBasedJobsData }));
    }
  }, [endDateBasedJobsData]);

  const handleChange = ({ target }) => {
    const name = target.name;
    let value = target.value;
    value = moment(
      `${moment(value).format("DD/MM/YYYY")} 11:59pm`,
      "DD/MM/YYYY hh:mma"
    ).toDate();

    setForm({ ...form, [name]: value });
  };

  useEffect(() => {
    load();
    loadSaleIncome();
  }, [memoryList, form.to, form.from, globalJobPaymentsData]);

  const loadSaleIncome = async () => {
    if (
      !memoryList.dueInvsData ||
      !memoryList.dueContInvs ||
      !globalJobPaymentsData
    )
      return;
    const { dueContInvs, dueInvsData } = memoryList;

    const globalPayments = [];
    const paymentDues = [];

    dueInvsData.forEach((job, ind) => {
      dueInvsData[ind].payments = [];
      globalJobPaymentsData?.forEach((payment) => {
        if (payment.job === job._id) {
          dueInvsData[ind].payments.push(payment);
        }
      });
      globalPayments.push(...job.payments);
      const tot = Number(removeCommas(job.total));
      const paid = sum(
        job.payments?.map((i) => Number(removeCommas(i.amount)))
      );
      if (Number(tot) - Number(paid) > 0) {
        const due = {
          invoiceID: job.invoiceID,
          invoiceDate: moment(job.invoiceDate).format("DD/MM/YYYY"),
          total: tot,
          paid,
          balance: tot - paid,
        };
        paymentDues.push(due);
      }
    });

    // invs.forEach((li) => {
    //   payments.push(...li.payments);
    //   const tot = Number(removeCommas(li.total));
    //   const paid = sum(li.payments.map((i) => Number(removeCommas(i.amount))));
    //   const due = {
    //     jobId: li.jobId,
    //     total: tot,
    //     paid,
    //     balance: tot - paid,
    //   };
    //   paymentDues.push(due);
    // });

    dueContInvs.forEach((li) => {
      globalPayments.push(...li.payments);
      const tot = li.total;
      const paid = sum(li.payments?.map((i) => Number(removeCommas(i.amount))));
      if (Number(tot) - Number(paid) > 0) {
        const due = {
          invoiceID: li.invoiceID,
          invoiceDate: moment(li.createdAt).format("DD/MM/YYYY"),
          total: tot,
          paid,
          balance: tot - paid,
        };
        paymentDues.push(due);
      }
    });

    const paids = sum(
      jobPaymentsData?.map((i) => Number(removeCommas(i.amount)) || 0)
    );

    const globalPaids = sum(
      globalPayments?.map((i) => Number(removeCommas(i.amount)) || 0)
    );

    const globalSum =
      sum(dueInvsData?.map((i) => Number(removeCommas(i.total)))) +
      sum(dueContInvs?.map((i) => i.total));

    const globalUnPaids = globalSum - globalPaids;

    setSalesIncome(paids);
    // setSalesIncomeList([...invs, contInvs]);
    setDuePayments(globalUnPaids);
    setDuePaymentsList(paymentDues);
  };

  const load = async () => {
    if (
      !memoryList.invs ||
      !memoryList.contInvs ||
      !memoryList.rebates ||
      !memoryList.expenses ||
      !memoryList.salary
    )
      return;

    const { rebates, expenses, salary, invs, contInvs } = memoryList;

    const list = [
      ...invs?.map((i) => ({ ...i, mode: "totalSales" })),
      ...contInvs?.map((i) => ({ ...i, mode: "totalSales" })),
      ...rebates?.map((i) => ({ ...i, mode: "rebate" })),
      ...expenses?.map((i) => ({ ...i, mode: "expense" })),
      ...salary?.map((i) => ({ ...i, mode: "salary" })),
    ];
    setData(list);
  };

  const handlePrint = () => {
    setIsPrintMode(true);

    // Small delay to ensure DOM updates before print
    setTimeout(() => {
      window.print();

      // Reset after print dialog closes
      // Note: This will run when dialog closes, not when printing finishes
      setTimeout(() => {
        setIsPrintMode(false);
      }, 1000);
    }, 100);
  };

  if (
    !Object.keys(memoryList).length ||
    !globalJobPaymentsData ||
    (globalJobPaymentsData?.length && !duePaymentsList.length)
  )
    return (
      <RiseLoader
        color={colors.primary}
        loading={true}
        style={{
          margin: "0 auto",
          borderColor: "red",
          display: "flex",
          justifyContent: "center",
          height: "calc(100vh - 100px)",
          alignItems: "center",
        }}
        size={30}
      />
    );

  // --- sales ----
  const sales = data?.filter((i) => i.mode === "totalSales");
  const saleSum = sum(
    sales?.map((i) => (i.subtotal ? Number(removeCommas(i.subtotal)) : i.price))
  );

  const salesWithHST = sales?.map((i) => {
    const hstAmount = i.subtotal
      ? (Number(removeCommas(i.subtotal)) * i.taxPercentage) / 100
      : i.price * i.tax;
    return { ...i, hstPercentage: i.taxPercentage || i.tax, hstAmount };
  });

  // const saleSumWithHST = sum(
  //   salesWithHST?.map((i) => {
  //     return i.hstAmount;
  //   })
  // );

  // ---- Rebates ----
  const rebates = data?.filter((i) => i.mode === "rebate");
  const rebateSum = sum(rebates?.map((i) => Number(removeCommas(i.amount))));

  // --- Payroll ---
  const salary = data?.filter((i) => i.mode === "salary");
  const salarySum = sum(salary?.map((i) => Number(removeCommas(i.amount))));

  // --- Expenses ----
  const expenses = data?.filter((i) => i.mode === "expense");
  const expenseGroup = Object.values(
    groupBy(expenses, (i) =>
      i.category === null ? " - " : i.category?.category
    )
  );

  const otherExpensesSum = sum(
    expenses?.map((i) => Number(removeCommas(i.amount)))
  );

  const expensesSum = otherExpensesSum + salarySum;
  const income = rebateSum + saleSum;

  return (
    <div>
      <Helmet>
        <title>{`Statement - ${moment(form.from).format(
          "DD/MM/YYYY"
        )} - ${moment(form.to).format("DD/MM/YYYY")}`}</title>
      </Helmet>

      {/* Print-specific styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            @page {
              size: letter;
              margin: 0.5in;
            }
          }
          
          .no-print {
            display: ${isPrintMode ? "none" : "block"};
          }
        `}
      </style>

      <div className="no-print">
        <Body
          back
          title="Statement"
          topRight={
            <div className="d-flex" style={{ gap: "0px 20px" }}>
              <AppButton
                tooltip="Print / Save as PDF"
                leftIcon={<AiFillPrinter size={25} />}
                onClick={handlePrint}
              />
            </div>
          }
        >
          <div>
            <br />
            <div className="d-flex" style={{ gap: 20, alignItems: "center" }}>
              <AppInput
                name="from"
                onChange={handleChange}
                label="From"
                floatLabel
                type="date"
                value={form.from}
                placeholder="Job Issue Date"
                maxDate={form.to}
              />
              <AppInput
                name="to"
                onChange={handleChange}
                label="To"
                floatLabel
                value={form.to}
                type="date"
                minDate={form.from}
              />
            </div>
            <div>
              Total Revenue
              <br />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontWeight: 500 }}
                leftTitle="Total Sales"
                rightTitle={`${addPrecision(numberFormat(saleSum))}`}
                children={
                  <Table
                    normalTable
                    header={salesHeader}
                    data={sales?.map((i) => {
                      return {
                        ...i,
                        organizationName: i.organization
                          ? organizations?.find(
                              (org) => i.organization === org._id
                            )?.name
                          : i.contract?.organizationName,
                        invoiceDate: moment(i.invoiceDate).format("DD/MM/YYYY"),
                        total: i.subtotal
                          ? addPrecision(numberFormat(i.subtotal))
                          : addPrecision(numberFormat(i.price)),
                      };
                    })}
                  />
                }
              />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontWeight: 500 }}
                leftTitle="Other Incomes"
                rightTitle={`${addPrecision(numberFormat(rebateSum))}`}
                children={
                  <Table
                    normalTable
                    header={rebatesHeader}
                    data={rebates?.map((i) => {
                      return {
                        ...i,
                        amount: addPrecision(numberFormat(i.amount)),
                        date: moment(i.date).format("DD/MM/YYYY"),
                      };
                    })}
                  />
                }
              />
              <br />
              <SimpleExpansionPanel
                accorditionStyle={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
                rightTitle={`${addPrecision(numberFormat(income))}`}
                leftTitle="Gross Income"
              />
              <br />
              <br />
              Cost of Sales
              <br />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontWeight: 500 }}
                leftTitle="Payroll"
                children={
                  <Table
                    header={salaryHeader}
                    normalTable
                    data={salary?.map((i) => {
                      return {
                        ...i,
                        date: moment(i.date).format("DD/MM/YYYY"),
                        amount: addPrecision(numberFormat(i.amount)),
                      };
                    })}
                  />
                }
                rightTitle={addPrecision(numberFormat(salarySum))}
              />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontWeight: 500 }}
                leftTitle="Other Expenses"
                rightTitle={addPrecision(numberFormat(otherExpensesSum))}
                children={
                  <Table
                    header={otherExpensesHeader}
                    normalTable
                    data={expenses?.map((i) => {
                      return {
                        ...i,
                        category: i.category?.category,
                        date: moment(i.date).format("DD/MM/YYYY"),
                        amount: addPrecision(numberFormat(i.amount)),
                      };
                    })}
                  />
                }
              />
              <br />
              <SimpleExpansionPanel
                rightTitle={`${addPrecision(numberFormat(expensesSum))}`}
                leftTitle="Total Expenses"
                accorditionStyle={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
              />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontSize: 22 }}
                rightTitleStyle={{ fontSize: 22 }}
                rightTitle={`${addPrecision(
                  numberFormat(income - expensesSum)
                )}`}
                leftTitle="Net Profit / Loss"
                accorditionStyle={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
              />
              <br />
              <br />
              Others
              <br />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontWeight: 500 }}
                rightTitle={endDateBasedJobsData?.length}
                leftTitle="No of completed jobs for the selected time period"
              />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontWeight: 500 }}
                leftTitle="Total sale's paid for given period (HST included)"
                rightTitle={addPrecision(numberFormat(salesIncome))}
              />
              <SimpleExpansionPanel
                leftTitleStyle={{ fontWeight: 500 }}
                leftTitle="Payment Dues"
                rightTitle={addPrecision(numberFormat(duePayments))}
              />
            </div>
          </div>
        </Body>
      </div>

      {/* Print-only content */}
      {isPrintMode && (
        <div className="print-content">
          <RebateStatementTemplate
            duePaymentsList={duePaymentsList}
            form={form}
            saleSum={saleSum}
            rebateSum={rebateSum}
            salarySum={salarySum}
            otherExpensesSum={otherExpensesSum}
            expensesSum={expensesSum}
            income={income}
            endDateBasedJobsData={endDateBasedJobsData}
            salesIncome={salesIncome}
            duePayments={duePayments}
            sales={sales}
            organizations={organizations}
            rebates={rebates}
            salary={salary}
            expenses={expenses}
            organizationsData={organizationsData}
            salesIncomeList={salesIncomeList}
          />
        </div>
      )}
    </div>
  );
}
